import { ApiError, del, get, patch, post, type Me, type TeamRow } from '../api.js';
import {
  button, chip, clear, drawer, el, empty, field, input, panel, row, select, skeleton, submitButton, toast,
} from '../ui.js';

interface JoinRequest {
  id: string; teamId: string; userId: string; message: string | null; status: string;
  createdAt: string; teamName: string; userName: string; userEmail: string; userState: string | null;
  decisionNote?: string | null;
}
interface TeamMemberRow {
  userId: string; name: string; email: string; role: 'leader' | 'member';
  state: string | null; joinedAt: string;
}
/**
 * What `GET /v1/teams/:id` actually sends back.
 *
 * `team` is the team row itself, not the search row: the detail endpoint has no
 * `memberCount`, no `leaderNames` and no `pendingRequests` on it. It was typed
 * as `TeamRow` here, so the members panel read `d.team.memberCount` off an
 * object that has never carried one and put "Members (undefined)" on screen.
 * The fields are listed rather than inherited so that cannot happen again.
 */
interface TeamDetail {
  team: Pick<TeamRow, 'id' | 'name' | 'description' | 'homeState' | 'homeCity' | 'joinPolicy'>;
  members: TeamMemberRow[];
  canManage: boolean;
  viewerRole: 'leader' | 'member' | null;
}
/** One row of `GET /v1/users` — the agency directory, behind `user.manage`. */
interface Person {
  id: string; firstName: string; lastName: string; email: string; status: string;
}

const SUB = 'font-size:12.5px;color:var(--ink-3);margin-top:12px';

const STATES: Array<[string, string]> = [['', 'Anywhere'], ...['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map((s) => [s, s] as [string, string])];

export function teamsView(me: Me): HTMLElement {
  const host = el('div');
  const canCreate = me.capabilities.includes('team.create');
  const canApprove = me.capabilities.includes('team.join.approve');

  const q = input({ type: 'search', placeholder: 'Team name, city or what they work' });
  const stateSel = select(STATES, me.user.state ?? '');
  const nearOnly = el('input', { type: 'checkbox', checked: me.user.state ? 'true' : null });
  const results = el('div');
  const inbox = el('div');
  const suggest = el('div');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Teams' }),
    el('h1', { text: 'Find a team' }),
    el('p', { text: 'A team decides who can see whose leads. A leader sees the whole team\'s book; a member sees only their own. Ask to join one near you, or start your own if you lead.' })));

  const bar = el('div', { class: 'filters' },
    field('Search', q),
    field('State', stateSel),
    field('Near me only', el('div', { style: 'height:38px;display:flex;align-items:center;gap:8px' },
      nearOnly, el('span', { style: 'font-size:12.5px;color:var(--ink-3)', text: me.user.state ? `Teams in ${me.user.state}` : 'Set your state in your profile first' }))));
  host.append(bar);

  if (canCreate) {
    host.append(el('div', { style: 'margin-bottom:14px' }, button('Create a team', 'pri', createTeam)));
  }

  host.append(inbox, suggest, results);

  let debounce: number | undefined;
  q.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void search(), 220);
  });
  stateSel.addEventListener('change', () => void search());
  nearOnly.addEventListener('change', () => void search());

  void search();
  void loadSuggested();
  if (canApprove) void loadInbox();

  /**
   * `GET /v1/teams/suggested`: at most six joinable teams the caller is not
   * already on — the ones in their own state first, then the rest of the
   * agency rather than an empty panel. It answers "which of these is mine?"
   * for somebody who has just arrived and does not yet know what to search
   * for, so it is hidden the moment they type a search of their own.
   */
  async function loadSuggested(): Promise<void> {
    let picks: TeamRow[];
    try {
      picks = await get<TeamRow[]>('/v1/teams/suggested');
    } catch (e) {
      // The search below is the real way through this page, so a failure here
      // says so once and leaves the rest of the screen alone.
      toast(e instanceof ApiError ? e.message : 'Could not load suggestions.', 'crit');
      return;
    }
    clear(suggest);
    if (picks.length === 0) return;
    suggest.append(panel('Suggested for you',
      el('p', { style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:10px', text: 'Teams that are taking people, and that you are not already on.' }),
      el('div', { class: 'rows' }, ...picks.map((t) => teamRow(t)))));
  }

  async function search(): Promise<void> {
    suggest.hidden = q.value.trim().length > 0;
    clear(results);
    results.append(skeleton(4));
    const params = new URLSearchParams();
    if (q.value.trim()) params.set('q', q.value.trim());
    if (stateSel.value) params.set('state', stateSel.value);
    if ((nearOnly as HTMLInputElement).checked) params.set('near', '1');
    let teams: TeamRow[];
    try {
      teams = await get<TeamRow[]>(`/v1/teams?${params.toString()}`);
    } catch (e) {
      clear(results);
      results.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not load teams.'));
      return;
    }
    clear(results);
    if (teams.length === 0) {
      results.append(panel(null, empty(
        'No teams match',
        (nearOnly as HTMLInputElement).checked
          ? 'Nothing in that state yet. Turn off "near me only" to see every team in the agency.'
          : canCreate ? 'Nobody has created a team yet. You can be first.' : 'Ask your manager to create one, or to add you to theirs.',
        canCreate ? button('Create a team', 'pri', createTeam) : undefined)));
      return;
    }
    const list = el('div', { class: 'rows' });
    for (const t of teams) list.append(teamRow(t));
    results.append(panel(`${teams.length} team${teams.length === 1 ? '' : 's'}`, list));
  }

  function teamRow(t: TeamRow): HTMLElement {
    const where = [t.homeCity, t.homeState].filter(Boolean).join(', ') || 'No home state';
    const leaders = t.leaderNames.length ? `Led by ${t.leaderNames.join(', ')}` : 'No leader';
    const right: HTMLElement[] = [];

    if (t.viewerRole === 'leader') right.push(chip('you lead this', 'cop'));
    else if (t.viewerRole === 'member') right.push(chip('you are on this', 'good'));
    else if (t.viewerRequestStatus === 'pending') right.push(chip('requested', 'warn'));
    else if (t.joinPolicy === 'closed') right.push(chip('invitation only'));
    else right.push(button(t.joinPolicy === 'open' ? 'Join' : 'Ask to join', 'sm pri', () => askToJoin(t)));

    if (t.pendingRequests > 0) right.unshift(chip(`${t.pendingRequests} waiting`, 'warn'));

    return row({
      title: t.name,
      sub: `${where} · ${t.memberCount} member${t.memberCount === 1 ? '' : 's'} · ${leaders}${t.description ? ` · ${t.description}` : ''}`,
      right,
      onclick: () => openTeam(t.id),
    });
  }

  async function askToJoin(t: TeamRow): Promise<void> {
    if (t.joinPolicy === 'open') {
      try {
        await post(`/v1/teams/${t.id}/join`, {});
        toast(`You are on ${t.name}.`, 'ok');
        void search();
        void loadSuggested();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not join.', 'crit'); }
      return;
    }
    const msg = el('textarea', { class: 'inp', rows: '4', placeholder: 'Which states are you licensed in? What do you write?' });
    const send = button('Send the request', 'pri');
    const close = drawer(`Ask to join ${t.name}`, el('div', null,
      el('p', { style: 'color:var(--ink-3);font-size:12.5px;margin-bottom:12px', text: 'A leader of this team will see your request and decide.' }),
      field('Message', msg),
      send));
    send.addEventListener('click', async () => {
      send.disabled = true;
      try {
        await post(`/v1/teams/${t.id}/join`, { message: msg.value || undefined });
        toast('Request sent.', 'ok');
        close();
        void search();
        void loadSuggested();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not send that.', 'crit'); send.disabled = false; }
    });
  }

  async function openTeam(id: string): Promise<void> {
    const body = el('div');
    body.append(skeleton(5));
    const closeDrawer = drawer('Team', body);

    /*
     * Who there is to add. `/v1/users` is the agency directory and it needs
     * `user.manage`, which only an owner or an administrator holds. A team
     * leader may add somebody through the API but has no endpoint that would
     * list the candidates, so they are not asked for the directory at all —
     * a 403 on every open would be the only thing that came of it. They get a
     * line saying so, and the approve-a-request path they do have, instead.
     *
     * Fetched once per opening rather than once per repaint: the roster comes
     * back fresh from the detail call, and who works at the agency does not
     * change between two clicks inside this drawer.
     */
    const canListPeople = me.capabilities.includes('user.manage');
    // Null means it was never asked for, or was asked for and did not arrive —
    // which is not the same thing as an agency with nobody in it.
    let people: Array<{ id: string; name: string }> | null = null;
    if (canListPeople) {
      try {
        people = (await get<Person[]>('/v1/users'))
          .filter((u) => u.status === 'active')
          .map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}`.trim() || u.email }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not load the people at this agency.', 'crit');
      }
    }

    async function paint(): Promise<void> {
      let d: TeamDetail;
      try {
        d = await get<TeamDetail>(`/v1/teams/${id}`);
      } catch (e) {
        clear(body);
        body.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not open that team.'));
        return;
      }
      clear(body);
      body.append(el('div', { style: 'margin-bottom:14px' },
        el('div', { style: 'font-family:var(--f-display);font-weight:700;font-size:19px', text: d.team.name }),
        el('div', { style: 'font-size:12.5px;color:var(--ink-3)', text: [d.team.homeCity, d.team.homeState].filter(Boolean).join(', ') || 'No home state' }),
        d.team.description ? el('p', { style: 'font-size:12.5px;color:var(--ink-2);margin-top:8px', text: d.team.description }) : null));

      const inside = Boolean(d.viewerRole) || d.canManage;
      if (!inside) {
        body.append(el('div', { class: 'note', style: 'margin-bottom:12px' },
          'You are not on this team, so you can see who leads it but not the full roster.'));
      }

      /*
       * A count only where there is one to give. Somebody outside the team is
       * sent the leaders and nobody else, so counting what arrived would state
       * the size of the team as the number of people who lead it.
       */
      body.append(panel(inside ? `Members (${d.members.length})` : 'Who leads this team',
        el('div', { class: 'rows' }, ...d.members.map((m) => memberRow(d, m, paint))),
        d.canManage ? adder(d, people, canListPeople, paint) : null));

      if (d.canManage) {
        let waiting: JoinRequest[] | null = null;
        try {
          waiting = (await get<JoinRequest[]>('/v1/join-requests')).filter((r) => r.teamId === d.team.id);
        } catch (e) {
          toast(e instanceof ApiError ? e.message : 'Could not load the requests to join.', 'crit');
        }
        body.append(panel(waiting ? `Requests to join (${waiting.length})` : 'Requests to join',
          waiting === null
            ? el('p', { style: 'color:var(--ink-3);font-size:12.5px', text: 'The requests did not load. This is not a list of nobody waiting — close this and open it again.' })
            : waiting.length === 0
              ? el('p', { style: 'color:var(--ink-3);font-size:12.5px', text: 'Nobody is waiting.' })
              : el('div', { class: 'rows' }, ...waiting.map(requestRow))));

        body.append(teamSettings(d, paint, () => { closeDrawer(); void search(); void loadSuggested(); }));
      }
    }

    await paint();
  }

  /** One person on the roster, with the controls whoever manages it may use. */
  function memberRow(d: TeamDetail, m: TeamMemberRow, repaint: () => Promise<void>): HTMLElement {
    const sub = [m.email, m.state].filter(Boolean).join(' · ') || '—';
    if (!d.canManage) {
      return row({
        title: m.name,
        sub,
        right: chip(m.role === 'leader' ? 'leader' : 'member', m.role === 'leader' ? 'cop' : ''),
      });
    }

    const roleSel = select([['leader', 'Leader'], ['member', 'Member']], m.role, {
      'aria-label': `Role on this team for ${m.name}`,
      style: 'width:auto;min-width:112px',
    });
    roleSel.addEventListener('change', async () => {
      const next = roleSel.value;
      if (next === m.role) return;
      roleSel.disabled = true;
      try {
        await patch(`/v1/teams/${encodeURIComponent(d.team.id)}/members/${encodeURIComponent(m.userId)}`, { role: next });
        toast(next === 'leader'
          ? `${m.name} leads ${d.team.name}.`
          : `${m.name} is a member of ${d.team.name}.`, 'ok');
        await repaint();
        void search();
      } catch (e) {
        // Put it back to what it was, so the screen never shows a role the
        // server refused — demoting the only leader is one of the refusals.
        roleSel.value = m.role;
        roleSel.disabled = false;
        toast(e instanceof ApiError ? e.message : 'Could not change that role.', 'crit');
      }
    });

    return row({
      title: m.name,
      sub,
      right: el('div', { style: 'display:flex;gap:8px;align-items:center;justify-content:flex-end;flex-wrap:wrap' },
        roleSel, removeButton(d, m, repaint)),
    });
  }

  /**
   * Taking somebody off a team, behind a second click.
   *
   * There is no undo on this screen and no confirmation from the server beyond
   * having done it, so the first click only arms the button and says what it is
   * about to do. It disarms itself a few seconds later: a button left sitting in
   * its "really?" state is one stray click away from removing somebody.
   */
  function removeButton(d: TeamDetail, m: TeamMemberRow, repaint: () => Promise<void>): HTMLButtonElement {
    const btn = button('Remove', 'sm danger');
    let armed = false;
    let timer: number | undefined;
    const disarm = (): void => {
      armed = false;
      window.clearTimeout(timer);
      btn.textContent = 'Remove';
    };

    btn.addEventListener('click', async () => {
      if (!armed) {
        armed = true;
        btn.textContent = 'Really remove?';
        timer = window.setTimeout(disarm, 5000);
        return;
      }
      disarm();
      btn.disabled = true;
      try {
        await del(`/v1/teams/${encodeURIComponent(d.team.id)}/members/${encodeURIComponent(m.userId)}`);
        toast(`${m.name} is no longer on ${d.team.name}.`, 'ok');
        await repaint();
        void search();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not remove them.', 'crit');
        btn.disabled = false;
      }
    });
    return btn;
  }

  /** The add-somebody form, for whoever may manage this team. */
  function adder(
    d: TeamDetail, people: Array<{ id: string; name: string }> | null,
    canListPeople: boolean, repaint: () => Promise<void>,
  ): HTMLElement {
    if (!canListPeople) {
      return el('p', { style: SUB },
        'Adding somebody straight to this team needs the agency directory, which an '
        + 'owner or administrator opens. Anyone here can ask to join instead, and you can approve them.');
    }
    if (people === null) {
      return el('p', { style: SUB },
        'The list of people at this agency did not load, so there is nobody here to pick from. '
        + 'Close this and open it again.');
    }
    const already = new Set(d.members.map((mm) => mm.userId));
    const free = people.filter((p) => !already.has(p.id));
    if (free.length === 0) {
      return el('p', { style: SUB }, 'Everybody active at this agency is already on this team.');
    }

    const who = select([['', 'Choose somebody'], ...free.map((p) => [p.id, p.name] as [string, string])], '');
    const asRole = select([['member', 'Member'], ['leader', 'Leader']], 'member');
    const whoField = field('Add somebody', who);
    const roleField = field('As', asRole, 'A leader sees the whole team’s book.');
    whoField.style.flex = '1 1 200px';
    roleField.style.flex = '0 1 170px';

    // Inside a form, so it submits with the keyboard as well as the mouse —
    // which is why this is `submitButton` and not `button`.
    const add = submitButton('Add', 'pri');
    add.style.marginBottom = '12px';

    const form = el('form', {
      style: 'display:flex;gap:10px;align-items:flex-end;flex-wrap:wrap;'
        + 'margin-top:14px;padding-top:14px;border-top:1px solid var(--rim)',
    }, whoField, roleField, add);

    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const pick = free.find((p) => p.id === who.value);
      if (!pick) { toast('Pick somebody to add.', 'crit'); return; }
      add.disabled = true;
      try {
        await post(`/v1/teams/${encodeURIComponent(d.team.id)}/members`, { userId: pick.id, role: asRole.value });
        toast(`${pick.name} is on ${d.team.name}.`, 'ok');
        await repaint();
        void search();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not add them to this team.', 'crit');
        add.disabled = false;
      }
    });
    return form;
  }

  function requestRow(r: JoinRequest): HTMLElement {
    const node = row({
      title: r.userName,
      sub: `${r.userEmail}${r.userState ? ` · ${r.userState}` : ''}${r.message ? ` — “${r.message}”` : ''}`,
      right: el('div', { style: 'display:flex;gap:6px' },
        button('Approve', 'sm pri', () => decide(r, 'approve', node)),
        button('Decline', 'sm ghost', () => decide(r, 'decline', node))),
    });
    return node;
  }

  async function decide(r: JoinRequest, action: 'approve' | 'decline', node: HTMLElement): Promise<void> {
    try {
      await post(`/v1/join-requests/${r.id}/${action}`, {});
      toast(action === 'approve' ? `${r.userName} is on ${r.teamName}.` : 'Request declined.', 'ok');
      node.remove();
      void loadInbox();
      void search();
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not do that.', 'crit'); }
  }

  async function loadInbox(): Promise<void> {
    let reqs: JoinRequest[];
    try { reqs = await get<JoinRequest[]>('/v1/join-requests'); } catch { return; }
    clear(inbox);
    if (reqs.length === 0) return;
    inbox.append(panel(`People waiting to join your teams (${reqs.length})`,
      el('div', { class: 'rows' }, ...reqs.map(requestRow))));
  }

  function createTeam(): void {
    const name = input({ required: true, placeholder: 'Southeast Desk' });
    const desc = el('textarea', { class: 'inp', rows: '3', placeholder: 'Final expense across Florida and Georgia.' });
    const state = select(STATES, me.user.state ?? '');
    const city = input({ placeholder: 'Tampa' });
    const policy = select([
      ['request', 'Anyone can ask; a leader approves'],
      ['open', 'Anyone in the agency can join instantly'],
      ['closed', 'Invitation only'],
    ], 'request');
    const save = button('Create the team', 'pri');
    const close = drawer('Create a team', el('div', null,
      el('p', { style: 'color:var(--ink-3);font-size:12.5px;margin-bottom:12px', text: 'You will lead it, so you can approve the first people who ask to join.' }),
      field('Name', name),
      field('What this team works', desc),
      el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
        field('Home state', state),
        field('City', city)),
      field('Who can join', policy),
      save));
    save.addEventListener('click', async () => {
      if (!name.value.trim()) { toast('Give the team a name.', 'crit'); return; }
      save.disabled = true;
      try {
        await post('/v1/teams', {
          name: name.value.trim(),
          description: desc.value || undefined,
          homeState: state.value || undefined,
          homeCity: city.value || undefined,
          joinPolicy: policy.value,
        });
        toast('Team created.', 'ok');
        close();
        void search();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not create that team.', 'crit'); save.disabled = false; }
    });
  }

  return host;
}


/* ---------------------------------------------------------- team settings -- */

/**
 * Renaming a team, and closing one.
 *
 * `PATCH` and `DELETE /v1/teams/:id` both existed with no button anywhere, so a
 * team created with a typo kept the typo forever and a team that stopped
 * existing stayed on everybody's list. Two routes, fully tested, unreachable.
 *
 * "Close" rather than "delete", because that is what the API does: the team is
 * archived, not destroyed. Leads, cases and history point at it, and a record
 * that vanishes takes their explanation with it. A button labelled Delete over
 * an endpoint that archives teaches people the wrong thing about what their
 * own data does when they press it.
 */
function teamSettings(
  d: TeamDetail, repaint: () => Promise<void>, close: () => void,
): HTMLElement {
  const name = input({ value: d.team.name, maxlength: '80' });
  const desc = input({ value: d.team.description ?? '', maxlength: '400', placeholder: 'optional' });
  const save = button('Save', 'pri');

  save.addEventListener('click', () => {
    const next = name.value.trim();
    if (next.length < 2) { toast('A team needs a name.', 'crit'); name.focus(); return; }
    save.disabled = true;
    void (async () => {
      try {
        await patch(`/v1/teams/${d.team.id}`, { name: next, description: desc.value.trim() });
        toast('Saved.', 'ok');
        await repaint();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not save that.', 'crit');
      } finally { save.disabled = false; }
    })();
  });

  const closeTeam = button('Close this team', 'danger');
  closeTeam.addEventListener('click', () => {
    if (!confirm(
      `Close ${d.team.name}?\n\nIt comes off everybody's list. Its leads, cases and `
      + 'history are kept and still point at it — this is not a delete.',
    )) return;
    closeTeam.disabled = true;
    void (async () => {
      try {
        await del(`/v1/teams/${d.team.id}`);
        toast('Team closed.', 'ok');
        close();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not close that team.', 'crit');
        closeTeam.disabled = false;
      }
    })();
  });

  return panel('Team settings',
    field('Name', name),
    field('Description', desc),
    el('div', { style: 'margin-bottom:16px' }, save),
    el('div', { class: 'note' },
      el('b', null, 'Closing keeps everything. '),
      'The team comes off the list; its leads, cases and history stay exactly where '
      + 'they are and still point at it.'),
    el('div', { style: 'margin-top:10px' }, closeTeam));
}
